// loginPage/Form.jsx
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Autocomplete,
  useMediaQuery,
  Typography,
  useTheme,
  Snackbar,
  Alert,
  Slider,
  Modal,
  IconButton,
  Avatar,
} from "@mui/material";
import { Close, Edit } from "@mui/icons-material";
import { Formik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setLogin } from "state";
import Cropper from "react-easy-crop";
import getCroppedImg from "../../utils/cropImage";
import { resolveAssetUrl } from "utils/media";
import { SCHOOLS } from "../../constants/schools";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
const ARTIST_NAME_REGEX = /^(?=.{2,40}$)[\p{L}\p{M}0-9._'\- ]+$/u;

const registerSchema = yup.object().shape({
  firstName: yup.string().required("Erforderlich"),
  lastName: yup.string().required("Erforderlich"),
  username: yup
    .string()
    .matches(/^[a-z0-9_]{3,30}$/, "Benutzername darf nur Kleinbuchstaben (a–z), Zahlen (0–9) und Unterstrich (_) enthalten (3–30 Zeichen)")
    .required("Erforderlich"),
  artistName: yup
    .string()
    .matches(
      ARTIST_NAME_REGEX,
      "Künstlername: 2-30 Zeichen, erlaubt sind Buchstaben, Zahlen, Leerzeichen sowie . _ ' -"
    )
    .required("Erforderlich"),
  school: yup.string().oneOf(["", ...SCHOOLS], "Bitte waehle eine Schule aus der Liste."),
  email: yup.string().email("Ungültige E-Mail").required("Erforderlich"),
  password: yup
    .string()
    .min(8, "Passwort muss mindestens 8 Zeichen lang sein")
    .required("Erforderlich"),
  picture: yup.mixed(),
});

const loginSchema = yup.object().shape({
  email: yup.string().email("Ungültige E-Mail").required("Erforderlich"),
  password: yup.string().required("Erforderlich"),
});

const initialValuesRegister = {
  firstName: "",
  lastName: "",
  username: "",
  artistName: "",
  school: "",
  email: "",
  password: "",
  picture: null,
};

const initialValuesLogin = {
  email: "",
  password: "",
};

const Form = () => {
  const [pageType, setPageType] = useState("login");
  const { palette } = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const isLogin = pageType === "login";
  const isRegister = pageType === "register";

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // "success" or "error"

  // Image cropping states
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImageUrl, setCroppedImageUrl] = useState("");
  const [showCropper, setShowCropper] = useState(false);

  useEffect(() => {
    return () => {
      if (croppedImageUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(croppedImageUrl);
      }
    };
  }, [croppedImageUrl]);

  const resendVerificationMail = async (email) => {
    try {
      const response = await fetch(`${BASE_URL}/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (!response.ok) {
        return { requestOk: false, emailSent: false };
      }
      return {
        requestOk: true,
        emailSent: data?.emailSent !== false,
      };
    } catch (error) {
      return { requestOk: false, emailSent: false };
    }
  };

  const register = async (values, onSubmitProps) => {
    try {
      const formData = new FormData();
      formData.append("firstName", values.firstName);
      formData.append("lastName", values.lastName);
      formData.append("email", values.email);
      formData.append("password", values.password);
      formData.append("username", values.username);
      formData.append("artistName", values.artistName.trim().replace(/\s+/g, " "));
      formData.append("school", values.school || "");

      if (values.picture) {
        formData.append("picture", values.picture, "profile.jpg");
        formData.append("picturePath", "profile.jpg");
      } else {
        // If no picture, set default picture path
        formData.append("picturePath", "/assets/misc/DefaultPFP.png");
      }

      const savedUserResponse = await fetch(`${BASE_URL}/auth/register`, {
        method: "POST",
        body: formData,
      });

      const savedUser = await savedUserResponse.json();

      if (!savedUserResponse.ok) {
        throw new Error(savedUser.message || "Registrierung fehlgeschlagen.");
      }

      onSubmitProps.resetForm();
      setCroppedImageUrl("");

      if (savedUser) {
        const registrationMessage =
          savedUser.message ||
          "Registrierung erfolgreich! Bitte bestätige zuerst deine E-Mail-Adresse und logge dich danach ein.";
        setSnackbarMessage(registrationMessage);
        setSnackbarSeverity(savedUser.emailSent === false ? "warning" : "success");
        setOpenSnackbar(true);
        setPageType("login");
      }
    } catch (err) {
      console.error("Registration failed:", err);
      setSnackbarMessage("Fehler bei der Registrierung: " + err.message);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  const login = async (values, onSubmitProps) => {
    try {
      const loggedInResponse = await fetch(`${BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const loggedIn = await loggedInResponse.json();
      if (!loggedInResponse.ok) {
        if (loggedIn.code === "EMAIL_NOT_VERIFIED") {
          const resendResult = await resendVerificationMail(values.email);
          const verifyMsg = resendResult.requestOk && resendResult.emailSent
            ? "Bitte bestätige zuerst deine E-Mail-Adresse. Wir haben dir einen neuen Verifizierungslink gesendet."
            : resendResult.requestOk
              ? "Bitte bestätige zuerst deine E-Mail-Adresse. Der erneute Versand war nicht erfolgreich."
            : "Bitte bestätige zuerst deine E-Mail-Adresse.";
          throw new Error(verifyMsg);
        }
        throw new Error(loggedIn.message || "Login fehlgeschlagen.");
      }
      onSubmitProps.resetForm();
      if (loggedIn) {
        dispatch(
          setLogin({
            user: loggedIn.user,
            token: loggedIn.token,
          })
        );
        navigate("/wettbewerb");
      }
    } catch (err) {
      console.error("Login failed:", err);
      setSnackbarMessage("Fehler beim Einloggen: " + err.message);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  const handleFormSubmit = async (values, onSubmitProps) => {
    if (isLogin) await login(values, onSubmitProps);
    if (isRegister) await register(values, onSubmitProps);
  };

  // Move readFile function outside since it doesn't depend on Formik
  const readFile = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.addEventListener("load", () => resolve(reader.result), false);
      reader.readAsDataURL(file);
    });
  };

  return (
    <>
      <Formik
        onSubmit={handleFormSubmit}
        initialValues={isLogin ? initialValuesLogin : initialValuesRegister}
        validationSchema={isLogin ? loginSchema : registerSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          resetForm,
        }) => {
          // Move onCropComplete inside Formik's render function
          const onCropComplete = async () => {
            try {
              const croppedImageBlob = await getCroppedImg(
                imageSrc,
                croppedAreaPixels
              );
              setCroppedImageUrl(URL.createObjectURL(croppedImageBlob));
              setFieldValue("picture", croppedImageBlob);
              setShowCropper(false);
            } catch (e) {
              console.error(e);
            }
          };

          // Move onFileChange inside Formik's render function
          const onFileChange = async (acceptedFiles) => {
            if (acceptedFiles && acceptedFiles.length > 0) {
              const file = acceptedFiles[0];
              const imageDataUrl = await readFile(file);
              setImageSrc(imageDataUrl);
              setCrop({ x: 0, y: 0 });
              setZoom(1);
              setShowCropper(true);
            }
          };

          const previewPictureSrc =
            croppedImageUrl || resolveAssetUrl("/assets/misc/DefaultPFP.png");
          const previewArtistName = values.artistName?.trim() || "Künstlername";
          const previewUsername = values.username?.trim() || "benutzername";

          return (
            <form onSubmit={handleSubmit}>
              <Box
                display="grid"
                gap="30px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                sx={{
                  "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                }}
              >
                {isRegister && (
                  <>
                    <TextField
                      label="Vorname"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.firstName}
                      name="firstName"
                      error={
                        Boolean(touched.firstName) && Boolean(errors.firstName)
                      }
                      helperText={touched.firstName && errors.firstName}
                      sx={{ gridColumn: "span 2" }}
                    />
                    <TextField
                      label="Name"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.lastName}
                      name="lastName"
                      error={
                        Boolean(touched.lastName) && Boolean(errors.lastName)
                      }
                      helperText={touched.lastName && errors.lastName}
                      sx={{ gridColumn: "span 2" }}
                    />
                    <Box gridColumn="span 4">
                      <TextField
                        label="Benutzername"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.username}
                        name="username"
                        error={
                          Boolean(touched.username) && Boolean(errors.username)
                        }
                        helperText={touched.username && errors.username}
                        sx={{ width: "100%", mb: 2 }}
                      />
                      <TextField
                        label="Künstlername"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.artistName}
                        name="artistName"
                        error={
                          Boolean(touched.artistName) && Boolean(errors.artistName)
                        }
                        helperText={touched.artistName && errors.artistName}
                        sx={{ width: "100%", mb: 2 }}
                      />
                      <Autocomplete
                        options={SCHOOLS}
                        value={values.school || null}
                        onChange={(_, value) => {
                          setFieldValue("school", value || "");
                        }}
                        onBlur={() => setFieldTouched("school", true)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Schule (optional)"
                            name="school"
                            error={Boolean(touched.school) && Boolean(errors.school)}
                            helperText={touched.school && errors.school}
                          />
                        )}
                        sx={{ width: "100%", mb: 2 }}
                      />
                      <Typography variant="subtitle1" gutterBottom>
                        Profil Vorschau
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.2,
                          p: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: "12px",
                          bgcolor: "background.alt",
                        }}
                      >
                        <Box
                          component="label"
                          sx={{
                            position: "relative",
                            cursor: "pointer",
                            display: "inline-flex",
                            borderRadius: "999px",
                          }}
                          aria-label="Profilbild auswählen"
                        >
                          <Avatar
                            src={previewPictureSrc}
                            sx={{ width: 52, height: 52 }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              inset: 0,
                              borderRadius: "50%",
                              bgcolor: "rgba(0, 0, 0, 0.45)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              opacity: 0,
                              transition: "opacity 0.2s ease",
                              pointerEvents: "none",
                              "& svg": { color: "#fff", fontSize: 18 },
                              "@media (hover: hover)": {
                                "label:hover &, label:focus-within &": {
                                  opacity: 1,
                                },
                              },
                            }}
                          >
                            <Edit />
                          </Box>
                          <input
                            type="file"
                            hidden
                            accept=".jpg,.jpeg,.png,.webp"
                            onChange={(e) => onFileChange(e.target.files)}
                          />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={600} fontSize="0.95rem" noWrap>
                            {previewArtistName}
                          </Typography>
                          <Typography fontSize="0.8rem" color="text.secondary" noWrap>
                            @{previewUsername}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.8 }}>
                        Tippe auf das Profilbild, um dein eigenes hinzuzufügen.
                      </Typography>
                    </Box>
                  </>
                )}

                {/* Email and Password Fields */}
                <TextField
                  label="E-Mail"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.email}
                  name="email"
                  error={Boolean(touched.email) && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  sx={{ gridColumn: "span 4" }}
                />
                <TextField
                  label="Passwort"
                  type="password"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.password}
                  name="password"
                  error={
                    Boolean(touched.password) && Boolean(errors.password)
                  }
                  helperText={touched.password && errors.password}
                  sx={{ gridColumn: "span 4" }}
                />
              </Box>
              <Modal
                open={showCropper}
                onClose={() => setShowCropper(false)}
                disableScrollLock
              >
                <Box
                  sx={{
                    position: "fixed",
                    inset: 0,
                    bgcolor: "rgba(0, 0, 0, 0.84)",
                    display: "flex",
                    alignItems: { xs: "stretch", sm: "center" },
                    justifyContent: "center",
                    p: { xs: 0, sm: 2 },
                    zIndex: 1400,
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: 720,
                      height: { xs: "100vh", sm: "82vh" },
                      bgcolor: "background.paper",
                      borderRadius: { xs: 0, sm: 2 },
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography fontWeight={600}>Profilbild zuschneiden</Typography>
                      <IconButton
                        onClick={() => setShowCropper(false)}
                        size="small"
                        aria-label="Schließen"
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box
                      sx={{
                        position: "relative",
                        flex: 1,
                        bgcolor: "rgba(0, 0, 0, 0.85)",
                      }}
                    >
                      {imageSrc && (
                        <Cropper
                          image={imageSrc}
                          crop={crop}
                          zoom={zoom}
                          aspect={1}
                          cropShape="round"
                          showGrid={false}
                          onCropChange={setCrop}
                          onZoomChange={setZoom}
                          onCropComplete={(_, areaPixels) => {
                            setCroppedAreaPixels(areaPixels);
                          }}
                          maxZoom={3}
                        />
                      )}
                    </Box>
                    <Box
                      sx={{
                        p: 2,
                        pb: "max(16px, env(safe-area-inset-bottom))",
                        borderTop: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Zoom
                      </Typography>
                      <Slider
                        value={zoom}
                        min={1}
                        max={3}
                        step={0.1}
                        onChange={(_, nextZoom) => setZoom(nextZoom)}
                        aria-label="Zoom"
                      />
                      <Box display="flex" justifyContent="space-between" gap={1}>
                        <Button
                          variant="outlined"
                          onClick={() => setShowCropper(false)}
                          sx={{ flex: 1 }}
                        >
                          Abbrechen
                        </Button>
                        <Button variant="contained" onClick={onCropComplete} sx={{ flex: 1 }}>
                          Zuschneiden
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Modal>

              {/* BUTTONS */}
              <Box>
                <Button
                  fullWidth
                  type="submit"
                  sx={{
                    m: "2rem 0",
                    p: "1rem",
                    backgroundColor: palette.primary.main,
                    color: palette.background.alt,
                    "&:hover": {
                      backgroundColor: palette.primary.dark,
                      color: palette.background.alt,
                    },
                  }}
                >
                  {isLogin ? "EINLOGGEN" : "REGISTRIEREN"}
                </Button>
                <Typography
                  onClick={() => {
                    setPageType(isLogin ? "register" : "login");
                    resetForm();
                  }}
                  sx={{
                    textDecoration: "underline",
                    color: palette.primary.main,
                    "&:hover": {
                      cursor: "pointer",
                      color: palette.primary.dark,
                    },
                  }}
                >
                  {isLogin
                    ? "Kein Konto? Hier registrieren."
                    : "Schon ein Konto? Hier anmelden."}
                </Typography>
                {isLogin && (
                  <Typography
                    onClick={() => navigate("/auth/passwort-vergessen")}
                    sx={{
                      mt: "0.6rem",
                      textDecoration: "underline",
                      color: palette.primary.main,
                      "&:hover": { cursor: "pointer", color: palette.primary.dark },
                    }}
                  >
                    Passwort vergessen?
                  </Typography>
                )}
              </Box>
            </form>
          );
        }}
      </Formik>

      {/* Snackbar for feedback messages */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Form;